import { Card, Checkbox, FormControlLabel, Grid } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ColorPickerInput from "components/ColorPickerInput";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import MDInput from "components/MDInput";
import { useLanguage } from "contexts/language.context";
import { useEffect, useMemo, useState } from "react";
import accountsService from "services/accounts-service";
import departmentsService from "services/departments-service";
import positionsService from "services/positions-service";
import rolesService from "services/roles-service";
import usersService from "services/users-service";
import useObjectState from "utils/useObjectState";

export default function ContactDialog({
  open,
  onClose,
  action,
  data,
  onSubmit,
}) {
  const { label } = useLanguage();
  const [loading, changeLoading] = useObjectState({ submit: false });
  const [value, setValue] = useState({});
  const [options, setOptions] = useState({});

  async function getOptions() {
    changeLoading("positions", true);
    changeLoading("departments", true);
    changeLoading("accounts", true);
    changeLoading("roles", true);

    const promises = [
      positionsService.getPositions(),
      departmentsService.getDepartments(),
      accountsService.getAccounts(),
      rolesService.getRoles(),
    ];

    const results = await Promise.all(promises);
    results[0].push("positions");
    results[1].push("departments");
    results[2].push("accounts");
    results[3].push("roles");

    for (let [response, data, key] of results) {
      if (response.ok) {
        setOptions((options) => ({ ...options, [key]: data }));
      }
    }

    changeLoading("positions", false);
    changeLoading("departments", false);
    changeLoading("accounts", false);
    changeLoading("roles", false);
  }

  async function getUser() {
    const [response, data] = await usersService.getUserByEmail(value.email);
    if (response.ok) {
      if (data) {
        setValue((value) => ({
          ...value,
          hasCredentials: data.contact.email == value.email,
          roleId: data.roleId,
          color: data.color,
          accountId: data.accountId,
          userId: data.id,
        }));
      }
    }
  }

  useEffect(() => {
    if (action == "create") {
      setValue({ accountId: data?.branch?.branchGroup?.accountId });
    } else {
      setValue(data?.contact);
    }

    changeLoading("submit", false);
  }, [data, open]);

  useEffect(() => {
    getOptions();
  }, []);

  // useEffect(() => {
  //   if (value?.email) {
  //     getUser();
  //   }
  // }, [value?.email]);

  const dropdownOptions = useMemo(() => {
    return {
      accounts:
        options.accounts?.map((value) => ({
          id: value.id,
          label: value.name,
        })) || [],
      positions:
        options.positions?.map((value) => ({
          id: value.id,
          label: value.name,
        })) || [],
      departments:
        options.departments?.map((value) => ({
          id: value.id,
          label: value.description,
        })) || [],
      roles:
        options.roles
          ?.sort((a, b) => a.account?.id - b.account?.id)
          ?.map((value) => ({
            id: value.id,
            label: value.description,
            account: value?.account?.name || "No account",
          })) || [],
    };
  }, [options]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();
          event.stopPropagation();

          changeLoading("submit", true);

          try {
            const result = await onSubmit(value);
            if (result.success) {
              onClose();
            }
          } catch (err) {
            console.error("onSubmit error:", err);
          }

          changeLoading("submit", false);
        },
      }}
    >
      <DialogTitle>
        {action == "edit" ? label("edit-contact") : label("create-contact")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
      >
        <Grid container spacing={1} mt={-0.5}>
          <Grid item xs={12}>
            <MDInput
              autoFocus
              required
              margin="dense"
              id="username-input"
              name="r"
              label={label("name-singular")}
              type="text"
              value={value?.name}
              onChange={(e) => {
                setValue({
                  ...value,
                  name: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              required
              margin="dense"
              id="useremail-input"
              name="r"
              label={label("email-singular")}
              type="email"
              value={value?.email}
              onChange={(e) => {
                setValue({
                  ...value,
                  email: e.target.value,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <DropdownSelect
              label={label("position-singular")}
              initialValue={value.contactPositionId || ""}
              onChange={(newValue) => {
                setValue((value) => ({
                  ...value,
                  contactPositionId: newValue?.id,
                }));
              }}
              items={dropdownOptions.positions}
              required={true}
              fullWidth
              loading={loading.positions}
            />
          </Grid>

          {/* <Grid item xs={12}>
            <Card sx={{ p: 2, pt: 0.5 }} variant="outlined">
              <Grid container spacing={1} alignItems={"center"}>
                <Grid item xs={12}>
                  <FormControlLabel
                    label="Has credentials"
                    control={
                      <Checkbox
                        checked={value.hasCredentials || false}
                        onChange={(e) => {
                          setValue((value) => ({
                            ...value,
                            hasCredentials: e.target.checked,
                          }));
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <DropdownSelect
                    label={label("department-singular")}
                    initialValue={value.departmentId || ""}
                    onChange={(newValue) => {
                      setValue((value) => ({
                        ...value,
                        departmentId: newValue?.id,
                      }));
                    }}
                    groupBy={(option) => option.account}
                    items={dropdownOptions.departments}
                    required={true}
                    fullWidth
                    loading={loading.departments}
                    disabled={!value.hasCredentials}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DropdownSelect
                    label={label("role-singular")}
                    initialValue={value.roleId || ""}
                    onChange={(newValue) => {
                      setValue((value) => ({
                        ...value,
                        roleId: newValue?.id,
                      }));
                    }}
                    groupBy={(option) => option.account}
                    items={dropdownOptions.roles}
                    required={true}
                    fullWidth
                    loading={loading.roles}
                    disabled={!value.hasCredentials}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ColorPickerInput
                    required
                    id="color"
                    name="color"
                    label={label("color-singular")}
                    type="color"
                    value={value.color || ""}
                    onChange={(e) => {
                      setValue((value) => ({
                        ...value,
                        color: e.target.value,
                      }));
                    }}
                    setValue={(newValue) => {
                      setValue((value) => ({
                        ...value,
                        color: newValue,
                      }));
                    }}
                    fullWidth
                    disabled={!value.hasCredentials}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DropdownSelect
                    label={label("account-singular")}
                    initialValue={value.accountId}
                    onChange={(newValue) => {
                      setValue((value) => ({
                        ...value,
                        accountId: newValue?.id,
                      }));
                    }}
                    items={dropdownOptions.accounts.filter(
                      (v) => v.id == data?.branch?.branchGroup?.accountId
                    )}
                    required={true}
                    fullWidth
                    loading={loading.accounts}
                    disabled={!value.hasCredentials}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid> */}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading.submit}
          type="submit"
          variant="gradient"
          color="info"
        >
          {action == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// import * as React from "react";
// import Button from "@mui/material/Button";
// import Dialog from "components/NoScrollDialog";
// import DialogActions from "@mui/material/DialogActions";
// import DialogContent from "@mui/material/DialogContent";
// import DialogTitle from "@mui/material/DialogTitle";
// import MDInput from "components/MDInput";
// import LoadingButton from "components/LoadingButton";
// import MSelect from "components/MSelect";
// import MDSelect from "components/MDSelect";
// import { Grid } from "@mui/material";
// import { useLanguage } from "contexts/language.context";

// export default function ContactDialog({ isOpen, action, data, onSubmit }) {
//   const { label } = useLanguage();
//   const [open, setOpen] = React.useState(false);
//   const [loading, setLoading] = React.useState(false);
//   const [value, setValue] = React.useState({
//     user: { name: "", email: "" },
//     contactPositionId: 0,
//   });

//   React.useEffect(() => {
//     if (!isOpen) return;
//     setOpen(true);
//     setLoading(false);
//   }, [isOpen]);

//   React.useEffect(() => {
//     if (action == "create") {
//       setValue({
//         user: { name: "", email: "" },
//         contactPositionId: data.positions[0].id,
//       });
//     } else {
//       setValue(data?.contact);
//     }
//   }, [data]);

//   const handleClose = () => {
//     setOpen(false);
//   };

//   return (
//     <Dialog
//       fullWidth
//       maxWidth="sm"
//       open={open}
//       onClose={handleClose}
//       PaperProps={{
//         component: "form",
//         onSubmit: async (event) => {
//           event.preventDefault();

//           setLoading(true);
//           const result = await onSubmit(value);
//           if (result.success) {
//             handleClose();
//           }

//           setLoading(false);
//         },
//       }}
//     >
//       <DialogTitle>
//         {action == "edit" ? label("edit-contact") : label("create-contact")}
//       </DialogTitle>
//       <DialogContent
//         sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
//       >
//         <Grid container spacing={1} mt={-0.5}>
//           <Grid item xs={12}>
//             <MDInput
//               autoFocus
//               required
//               margin="dense"
//               id="username-input"
//               name="r"
//               label={label("name-singular")}
//               type="text"
//               value={value.user?.name}
//               onChange={(e) => {
//                 setValue({
//                   ...value,
//                   user: { ...value.user, name: e.target.value },
//                 });
//               }}
//               fullWidth
//             />
//           </Grid>
//           <Grid item xs={12}>
//             <MDInput
//               autoFocus
//               required
//               margin="dense"
//               id="useremail-input"
//               name="r"
//               label={label("email-singular")}
//               type="email"
//               value={value.user?.email}
//               onChange={(e) => {
//                 setValue({
//                   ...value,
//                   user: { ...value.user, email: e.target.value },
//                 });
//               }}
//               fullWidth
//             />
//           </Grid>
//           <Grid item xs={12}>
//             <MSelect
//               items={data.positions.map((position) => {
//                 return {
//                   value: position.id,
//                   render: position.name,
//                 };
//               })}
//               id="contact position-select"
//               value={value.contactPositionId}
//               onChange={(e) => {
//                 setValue({ ...value, contactPositionId: e });
//               }}
//               label={label("position-singular")}
//               required
//             />
//           </Grid>
//         </Grid>
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={handleClose}>{label("cancel-button")}</Button>
//         <LoadingButton
//           loading={loading}
//           type="submit"
//           variant="gradient"
//           color="info"
//         >
//           {action == "edit" ? label("save-button") : label("create-button")}
//         </LoadingButton>
//       </DialogActions>
//     </Dialog>
//   );
// }
